import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MaterialReactTable } from "material-react-table";
import {
  mrtBodyCellSx,
  mrtHeadCellSx,
  mrtSurfaceProps,
} from "../../../../../../styles/mrtThemeProps";
import { usePiping } from "../../PipingContext";
import { getFamilyTableOptions } from "./utils";
import { EditableSelectCell } from "../EditableSelectCell";

// ─── Family Table cell for the dialog ─────────────────────────────────────────
function DialogFamilyTableCell({
  row,
  allTables,
  fullSpecTable,
  onFamilyTableChange,
}) {
  const Component = row.original.Components;
  const spec = row.original.Spec;
  const value = row.original["Family Table"] || "";

  const schedRating = row.original["Schedule/Rating"];
  const ends = row.original["Ends"];
  const material = row.original["Material"];
  const addNotes = row.original["Additional Notes"];

  const fullOptions = useMemo(() => {
    if (!Component) return [];
    return getFamilyTableOptions(
      Component,
      spec,
      {
        "Schedule/Rating": schedRating,
        Ends: ends,
        Material: material,
        "Additional Notes": addNotes,
      },
      fullSpecTable,
    );
  }, [
    Component,
    spec,
    schedRating,
    ends,
    material,
    addNotes,
    fullSpecTable,
  ]);

  const nameOptions = useMemo(() => {
    const seen = new Set();
    return (fullOptions || [])
      .map((cur) => String(cur?.Family_Name ?? "").trim())
      .filter((n) => {
        if (!n || n === "-") return false;
        if (seen.has(n)) return false;
        seen.add(n);
        return true;
      });
  }, [fullOptions]);

  const safeValue = nameOptions.includes(value) ? value : "";

  // Mirror FamilyTableCell's auto-selection: when a row arriving from search
  // has exactly one valid Family Name and no existing value, select it so the
  // dialog stays consistent with the main tab view.
  const lastAutoSelected = useRef(null);

  useEffect(() => {
    if (!Component) return;
    if (nameOptions.length !== 1) return;
    const onlyOption = nameOptions[0];
    if (value === onlyOption) return;
    if (lastAutoSelected.current === onlyOption) return;
    lastAutoSelected.current = onlyOption;
    onFamilyTableChange(row.original, onlyOption, fullOptions);
  }, [Component, nameOptions, value, row, fullOptions, onFamilyTableChange]);

  if (!Component) return <EditableSelectCell disabled />;

  return (
    <EditableSelectCell
      value={safeValue}
      options={nameOptions}
      onChange={(newVal) =>
        onFamilyTableChange(row.original, newVal, fullOptions)
      }
    />
  );
}

// ─── Row identity helpers ─────────────────────────────────────────────────────
const rowMatches = (a, b) =>
  a.Size === b.Size &&
  a.Spec === b.Spec &&
  a.Components === b.Components &&
  a.Material === b.Material &&
  a.Ends === b.Ends &&
  a["Schedule/Rating"] === b["Schedule/Rating"] &&
  a["Additional Notes"] === b["Additional Notes"] &&
  a.TabName === b.TabName;

const getRowKey = (r) =>
  `${r.Spec}|${r.Components}|${r.Size}|${r.Material}|${r.Ends}|${r["Schedule/Rating"]}|${r["Additional Notes"]}|${r.TabName}`;

const SEARCH_COLS = ["Size", "Schedule/Rating", "Ends", "Material"];

const getInitialSelectedValuesByCol = () => ({
  Size: [],
  "Schedule/Rating": [],
  Ends: [],
  Material: [],
});

// ─── BulkSelectDialog ─────────────────────────────────────────────────────────
export function BulkSelectDialog({
  open,
  onClose,
  revision,
  currentTabData,
  activeTabName,
}) {
  const {
    allCombinationTable,
    setAllCombinationTable,
    setPartLibTable,
    allTables,
    fullSpecTable,
  } = usePiping();

  const [selectedValuesByCol, setSelectedValuesByCol] = useState(
    getInitialSelectedValuesByCol(),
  );
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localSelections, setLocalSelections] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ─── Selected template rows from active tab ────────────────────────────────
  const selectedTemplateRows = useMemo(
    () => (currentTabData || []).filter((r) => r.select === true),
    [currentTabData],
  );

  const hasNoTemplates = selectedTemplateRows.length === 0;

  // ─── Reset on close ─────────────────────────────────────────────────────────
  const resetDialogState = useCallback(() => {
    setSelectedValuesByCol(getInitialSelectedValuesByCol());
    setResults([]);
    setHasSearched(false);
    setLoading(false);
    setLocalSelections({});
    setSnackbar({
      open: false,
      message: "",
      severity: "success",
    });
  }, []);

  const handleDialogClose = useCallback(() => {
    resetDialogState();
    onClose?.();
  }, [resetDialogState, onClose]);

  // ─── Unique values from selected template rows ─────────────────────────────
  const uniqueValuesByCol = useMemo(() => {
    const map = {};

    SEARCH_COLS.forEach((col) => {
      const vals = new Set();

      selectedTemplateRows.forEach((r) => {
        const v = String(r[col] ?? "").trim();
        if (v) vals.add(v);
      });

      map[col] = [...vals].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      );
    });

    return map;
  }, [selectedTemplateRows]);

  const activeCols = useMemo(
    () =>
      SEARCH_COLS.filter(
        (col) =>
          Array.isArray(selectedValuesByCol[col]) &&
          selectedValuesByCol[col].length > 0,
      ),
    [selectedValuesByCol],
  );

  // ─── Search value toggles ───────────────────────────────────────────────────
  const handleToggleSearchValue = useCallback((col, value, checked) => {
    setSelectedValuesByCol((prev) => {
      const current = prev[col] || [];
      return {
        ...prev,
        [col]: checked
          ? [...current, value]
          : current.filter((v) => v !== value),
      };
    });
  }, []);

  // ─── Search ────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(() => {
    setLoading(true);

    if (activeCols.length === 0) {
      setResults([]);
      setLocalSelections({});
      setHasSearched(true);
      setLoading(false);
      return;
    }

    // AND across columns, OR within a column's selected values
    const filtered = allCombinationTable.filter((row) =>
      activeCols.every((col) => {
        const rowValue = String(row[col] ?? "").trim();
        return selectedValuesByCol[col].includes(rowValue);
      }),
    );

    const selMap = {};
    filtered.forEach((row) => {
      selMap[getRowKey(row)] = row.select || false;
    });

    setResults(filtered);
    setLocalSelections(selMap);
    setHasSearched(true);
    setLoading(false);
  }, [allCombinationTable, activeCols, selectedValuesByCol]);

  // ─── Row selection toggles ─────────────────────────────────────────────────
  const handleToggleRow = useCallback((row, checked) => {
    setLocalSelections((prev) => ({ ...prev, [getRowKey(row)]: checked }));
  }, []);

  const handleSelectAll = useCallback(() => {
    const all = {};
    results.forEach((r) => {
      all[getRowKey(r)] = true;
    });
    setLocalSelections(all);
  }, [results]);

  const handleDeselectAll = useCallback(() => {
    const none = {};
    results.forEach((r) => {
      none[getRowKey(r)] = false;
    });
    setLocalSelections(none);
  }, [results]);

  const selectedRowsInResults = useMemo(
    () => results.filter((r) => localSelections[getRowKey(r)]).length,
    [results, localSelections],
  );

  const allRowsSelected =
    results.length > 0 && selectedRowsInResults === results.length;

  const someRowsSelected =
    selectedRowsInResults > 0 && selectedRowsInResults < results.length;

  const handleToggleAllFromHeader = useCallback(
    (checked) => {
      if (checked) {
        handleSelectAll();
      } else {
        handleDeselectAll();
      }
    },
    [handleSelectAll, handleDeselectAll],
  );

  // ─── Family Table change ───────────────────────────────────────────────────
  const handleFamilyTableChange = useCallback(
    (rowData, newVal, fullOptions) => {
      const tableTarget =
        fullOptions.find((cur) => cur.Family_Name === newVal)?.Family_Table ||
        "";

      setResults((prev) =>
        prev.map((r) =>
          rowMatches(r, rowData)
            ? { ...r, "Family Table": newVal, FamilyTableTarget: tableTarget }
            : r,
        ),
      );

      setAllCombinationTable((prev) =>
        prev.map((r) =>
          rowMatches(r, rowData)
            ? { ...r, "Family Table": newVal, FamilyTableTarget: tableTarget }
            : r,
        ),
      );

      if (rowData.select) {
        setPartLibTable((prev) => {
          const updatedRevision = { ...(prev[revision] || {}) };
          const tabName = rowData.TabName;

          const updatedTab = (updatedRevision[tabName] || []).map((r) =>
            r.Size === rowData.Size &&
            r.Spec === rowData.Spec &&
            r.Components === rowData.Components &&
            r.Material === rowData.Material &&
            r.Ends === rowData.Ends &&
            r["Schedule/Rating"] === rowData["Schedule/Rating"]
              ? { ...r, "Family Table": newVal, FamilyTableTarget: tableTarget }
              : r,
          );

          return {
            ...prev,
            [revision]: { ...updatedRevision, [tabName]: updatedTab },
          };
        });
      }
    },
    [revision, setAllCombinationTable, setPartLibTable],
  );

  // ─── Save selections ───────────────────────────────────────────────────────
  const handleSaveSelections = useCallback(() => {
    const selectionMap = {};
    results.forEach((row) => {
      selectionMap[getRowKey(row)] =
        localSelections[getRowKey(row)] ?? row.select ?? false;
    });

    setAllCombinationTable((prev) =>
      prev.map((row) => {
        const key = getRowKey(row);
        if (!(key in selectionMap)) return row;
        return { ...row, select: selectionMap[key] };
      }),
    );

    setSnackbar({
      open: true,
      message: "Selections saved successfully!",
      severity: "success",
    });
  }, [results, localSelections, setAllCombinationTable]);

  // ─── Column definitions ────────────────────────────────────────────────────
  const dialogColumns = useMemo(
    () => [
      {
        id: "select",
        header: "Select",
        Header: () => (
          <Checkbox
            size="small"
            checked={allRowsSelected}
            indeterminate={someRowsSelected}
            disabled={results.length === 0}
            onChange={(e) => handleToggleAllFromHeader(e.target.checked)}
          />
        ),
        enableHiding: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 55,
        Cell: ({ row }) => (
          <Checkbox
            size="small"
            checked={localSelections[getRowKey(row.original)] || false}
            onChange={(e) => handleToggleRow(row.original, e.target.checked)}
          />
        ),
      },
      {
        accessorKey: "TabName",
        header: "Tab",
        size: 70,
        enableColumnActions: false,
      },
      {
        accessorKey: "Components",
        header: "Component",
        size: 100,
        enableColumnActions: false,
      },
      {
        accessorKey: "Size",
        header: "Size",
        size: 55,
        enableColumnActions: false,
      },
      {
        accessorKey: "Schedule/Rating",
        header: "Sch/Rating",
        size: 75,
        enableColumnActions: false,
      },
      {
        accessorKey: "Ends",
        header: "Ends",
        size: 55,
        enableColumnActions: false,
      },
      {
        accessorKey: "Material",
        header: "Material",
        size: 65,
        enableColumnActions: false,
      },
      {
        accessorKey: "Material Description",
        header: "Material Description",
        size: 170,
        enableColumnActions: false,
      },
      {
        accessorKey: "Family Table",
        header: "Family Table",
        size: 150,
        enableColumnActions: false,
        Cell: (props) => (
          <DialogFamilyTableCell
            {...props}
            fullSpecTable={fullSpecTable}
            onFamilyTableChange={handleFamilyTableChange}
          />
        ),
      },
    ],
    [
      allRowsSelected,
      someRowsSelected,
      results.length,
      localSelections,
      allTables,
      fullSpecTable,
      handleFamilyTableChange,
      handleToggleAllFromHeader,
      handleToggleRow,
    ],
  );

  const selectedCount = Object.values(localSelections).filter(Boolean).length;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "92vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            minHeight: 48,
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
            Bulk Select
          </Typography>

          <IconButton onClick={handleDialogClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            px: 2,
            py: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: "0 1 auto",
              minHeight: 0,
              maxHeight: "32vh",
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            <Box sx={{ mb: 0.25 }}>
              <Typography variant="body2" fontWeight="bold">
                Search source:{" "}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  {activeTabName} tab — {selectedTemplateRows.length} selected
                  row
                  {selectedTemplateRows.length !== 1 ? "s" : ""} used as
                  templates
                </Typography>
              </Typography>

              {hasNoTemplates && (
                <Alert severity="warning" sx={{ mt: 0.75, py: 0 }}>
                  No rows are selected in the <strong>{activeTabName}</strong>{" "}
                  tab. Select at least one row there before using Bulk Select.
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 0.75 }} />

            <Box sx={{ mb: 0.25 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.75 }}>
                Match by values
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  — choose the values you want under each field
                </Typography>
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                {SEARCH_COLS.map((col) => (
                  <Box
                    key={col}
                    sx={{
                      minWidth: 155,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ mb: 0.5 }}
                    >
                      {col}
                    </Typography>

                    {uniqueValuesByCol[col].length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                        }}
                      >
                        {uniqueValuesByCol[col].map((value) => (
                          <FormControlLabel
                            key={`${col}-${value}`}
                            sx={{ m: 0, minHeight: 28 }}
                            control={
                              <Checkbox
                                size="small"
                                disabled={hasNoTemplates}
                                checked={(
                                  selectedValuesByCol[col] || []
                                ).includes(value)}
                                onChange={(e) =>
                                  handleToggleSearchValue(
                                    col,
                                    value,
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ lineHeight: 1.1 }}
                              >
                                {value}
                              </Typography>
                            }
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="warning.main">
                        No values in selected rows
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  mt: 0.75,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSearch}
                  disabled={
                    hasNoTemplates || activeCols.length === 0 || loading
                  }
                  startIcon={loading ? <CircularProgress size={14} /> : null}
                >
                  Search
                </Button>

                {!hasNoTemplates && activeCols.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Select at least one value to enable search.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 0 }} />

          {hasSearched && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                Results — {selectedCount} selected / {results.length} found
              </Typography>

              {results.length === 0 ? (
                <Alert severity="info" sx={{ py: 0 }}>
                  No rows matched the selected values across any tab.
                </Alert>
              ) : (
                <MaterialReactTable
                  columns={dialogColumns}
                  data={results}
                  enableSorting={false}
                  enableTopToolbar={false}
                  enableBottomToolbar={true}
                  initialState={{ pagination: { pageSize: 25, pageIndex: 0 } }}
                  muiTablePaperProps={{
                    sx: {
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    },
                  }}
                  {...mrtSurfaceProps}
                  muiTableContainerProps={{
                    sx: {
                      flex: 1,
                      minHeight: 0,
                      backgroundColor: "background.paper",
                    },
                  }}
                  muiBottomToolbarProps={{
                    sx: {
                      position: "sticky",
                      bottom: 0,
                      zIndex: 2,
                      backgroundColor: "background.paper",
                      borderTop: 1,
                      borderColor: "divider",
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      ...mrtBodyCellSx,
                      borderRight: 1,
                      fontSize: "0.65rem",
                      padding: "0px 3px !important",
                      overflow: "visible",
                    },
                  }}
                  muiTableHeadCellProps={{
                    sx: {
                      ...mrtHeadCellSx,
                      fontWeight: "bold",
                      fontSize: "0.65rem",
                      borderRight: 1,
                      padding: "2px 4px",
                    },
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1,
            gap: 1,
          }}
        >
          <Button onClick={handleDialogClose} color="inherit" size="small">
            Close
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveSelections}
            disabled={!hasSearched || results.length === 0}
          >
            Save Selections
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}